/**
 * The single source of truth for how the spreadsheet is laid out.
 *
 * Column letters appear in this file and nowhere else in the codebase.
 *
 * Verified against the live sheet on 2026-09-17. The TASKS tab has its headers
 * on ROW 5 and its data from ROW 6. Rows 1-4 hold a title, a legend, and a
 * `=NOW()` cell at D3 that the "Due days" formula depends on.
 *
 *   A  (empty spacer)
 *   B  Project           project name, human readable, kept in sync with P
 *   C  Activity          free text, pre-existing
 *   D  Cooperate with
 *   E  Task              the title
 *   F  Deadline          real date value
 *   G  Due days          FORMULA COLUMN - NEVER WRITTEN. See below.
 *   H  Date done         real date value
 *   I  Result 1          the result link
 *   J  Note
 *   K  calendarEventId   mapped, reserved, unused until calendar sync lands
 *   L  TaskID            identity
 *   M  Status            data-validation dropdown
 *   N  Work package      added by setup
 *   O  Deleted           added by setup, soft-delete marker
 *   P  ProjectID         added by setup, the stable link to the Projects tab
 *   Q  Outcome           added by setup, the "no deliverable" reason
 *
 * Column G holds `=If(isblank(Hn), if(Fn<>"", int(Fn-$D$3)+1, ""), "")`. Only
 * 50 of 154 rows still had the formula when audited; the rest had been
 * overwritten with pasted static numbers. The app neither reads nor writes it,
 * and computes overdue from the deadline itself. Writing a full-width row
 * would blank it, which is why writes are split around it.
 */
import type { Project, Task } from "../types";
import { isStatus } from "../types";
import { isoToSerial, serialToIso } from "./dates";

/* ------------------------------------------------------------------ tasks */

export const TASKS_HEADER_ROW = 5;
export const TASKS_FIRST_DATA_ROW = 6;

/** The span the app reads. Starts at B: column A is always empty. */
export const TASKS_READ_FIRST = "B";
export const TASKS_READ_LAST = "Q";

/** The formula column, excluded from every write. */
export const FORMULA_COLUMN = "G";

export const DELETED_MARKER = "DELETED";

export const TASK_COLUMNS = {
  projectName: "B",
  activity: "C",
  cooperateWith: "D",
  title: "E",
  deadline: "F",
  dateDone: "H",
  resultLink: "I",
  notes: "J",
  calendarEventId: "K",
  id: "L",
  status: "M",
  workPackage: "N",
  deleted: "O",
  projectId: "P",
  outcome: "Q",
} as const;

export type TaskField = keyof typeof TASK_COLUMNS;

/** Headers the setup step writes for the columns it adds. */
export const ADDED_TASK_HEADERS: ReadonlyArray<[string, string]> = [
  [TASK_COLUMNS.workPackage, "Work package"],
  [TASK_COLUMNS.deleted, "Deleted"],
  [TASK_COLUMNS.projectId, "ProjectID"],
  [TASK_COLUMNS.outcome, "Outcome"],
];

export function columnNumber(letter: string): number {
  let n = 0;
  for (const ch of letter.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n;
}

export function columnLetter(n: number): string {
  let s = "";
  let v = n;
  while (v > 0) {
    const r = (v - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    v = Math.floor((v - 1) / 26);
  }
  return s;
}

/** Index into a row array returned for the read range. */
function offset(letter: string): number {
  return columnNumber(letter) - columnNumber(TASKS_READ_FIRST);
}

function text(row: unknown[], letter: string): string {
  const v = row[offset(letter)];
  if (v === null || v === undefined) return "";
  return String(v).trim();
}

/**
 * True when a row carries no task at all.
 *
 * Rows 63, 65 and 66 of the live sheet hold nothing but a leftover Due days
 * formula. They are spacers, not tasks, and must never receive a TaskID.
 */
export function isBlankRow(row: unknown[]): boolean {
  const meaningful: string[] = [
    TASK_COLUMNS.projectName,
    TASK_COLUMNS.activity,
    TASK_COLUMNS.cooperateWith,
    TASK_COLUMNS.title,
    TASK_COLUMNS.deadline,
    TASK_COLUMNS.dateDone,
    TASK_COLUMNS.resultLink,
    TASK_COLUMNS.notes,
    TASK_COLUMNS.status,
    TASK_COLUMNS.id,
  ];
  return meaningful.every((letter) => text(row, letter) === "");
}

export function rowToTask(row: unknown[], sheetRow: number): Task {
  const status = text(row, TASK_COLUMNS.status);
  return {
    id: text(row, TASK_COLUMNS.id),
    projectId: text(row, TASK_COLUMNS.projectId),
    projectName: text(row, TASK_COLUMNS.projectName),
    title: text(row, TASK_COLUMNS.title),
    activity: text(row, TASK_COLUMNS.activity),
    workPackage: text(row, TASK_COLUMNS.workPackage),
    cooperateWith: text(row, TASK_COLUMNS.cooperateWith),
    deadline: serialToIso(row[offset(TASK_COLUMNS.deadline)]),
    dateDone: serialToIso(row[offset(TASK_COLUMNS.dateDone)]),
    notes: text(row, TASK_COLUMNS.notes),
    status: isStatus(status) ? status : "Not Started",
    resultLink: text(row, TASK_COLUMNS.resultLink),
    outcome: text(row, TASK_COLUMNS.outcome),
    calendarEventId: text(row, TASK_COLUMNS.calendarEventId),
    deleted: text(row, TASK_COLUMNS.deleted).toUpperCase() === DELETED_MARKER,
    sheetRow,
  };
}

/** True when the sheet says nothing about status, so the app must not guess. */
export function hasExplicitStatus(row: unknown[]): boolean {
  return isStatus(text(row, TASK_COLUMNS.status));
}

export type CellValue = string | number;

export interface RangeUpdate {
  range: string;
  values: CellValue[];
}

function cellValue(task: Task, field: TaskField): CellValue {
  switch (field) {
    case "deadline":
      return isoToSerial(task.deadline);
    case "dateDone":
      return isoToSerial(task.dateDone);
    case "deleted":
      return task.deleted ? DELETED_MARKER : "";
    default:
      return task[field as Exclude<TaskField, "deadline" | "dateDone" | "deleted">];
  }
}

/**
 * Every cell the app owns, as contiguous ranges that skip column G.
 *
 * Splitting here rather than writing B:Q in one shot is the whole reason the
 * Due days formulas survive an edit.
 */
export function taskUpdates(tab: string, task: Task, row: number): RangeUpdate[] {
  const byLetter = new Map<string, CellValue>();
  for (const field of Object.keys(TASK_COLUMNS) as TaskField[]) {
    byLetter.set(TASK_COLUMNS[field], cellValue(task, field));
  }

  const updates: RangeUpdate[] = [];
  let runStart: string | null = null;
  let run: CellValue[] = [];

  const flush = () => {
    if (runStart && run.length) {
      const end = columnLetter(columnNumber(runStart) + run.length - 1);
      updates.push({ range: `${tab}!${runStart}${row}:${end}${row}`, values: run });
    }
    runStart = null;
    run = [];
  };

  const first = columnNumber(TASKS_READ_FIRST);
  const last = columnNumber(TASKS_READ_LAST);
  for (let n = first; n <= last; n++) {
    const letter = columnLetter(n);
    if (letter === FORMULA_COLUMN || !byLetter.has(letter)) {
      flush();
      continue;
    }
    if (!runStart) runStart = letter;
    run.push(byLetter.get(letter)!);
  }
  flush();
  return updates;
}

export function taskCell(tab: string, field: TaskField, row: number): string {
  return `${tab}!${TASK_COLUMNS[field]}${row}`;
}

export function tasksReadRange(tab: string): string {
  return `${tab}!${TASKS_READ_FIRST}${TASKS_FIRST_DATA_ROW}:${TASKS_READ_LAST}`;
}

export function tasksHeaderRange(tab: string): string {
  return `${tab}!${TASKS_READ_FIRST}${TASKS_HEADER_ROW}:${TASKS_READ_LAST}${TASKS_HEADER_ROW}`;
}

/* --------------------------------------------------------------- projects */

export const PROJECTS_HEADER_ROW = 1;
export const PROJECTS_FIRST_DATA_ROW = 2;
export const PROJECTS_READ_FIRST = "A";
export const PROJECTS_READ_LAST = "G";

export const PROJECT_COLUMNS = {
  id: "A",
  name: "B",
  color: "C",
  archived: "D",
  mindmap: "E",
  taskFile: "F",
  driveFolder: "G",
} as const;

export const PROJECT_HEADERS = [
  "ProjectID",
  "Name",
  "Colour",
  "Archived",
  "Mindmap",
  "Task file",
  "Project drive",
];

/** Headers added after the Projects tab first shipped, written by setup. */
export const ADDED_PROJECT_HEADERS: ReadonlyArray<[string, string]> = [
  ["F", "Task file"],
  ["G", "Project drive"],
];

function projectOffset(letter: string): number {
  return columnNumber(letter) - columnNumber(PROJECTS_READ_FIRST);
}

function projectText(row: unknown[], letter: string): string {
  const v = row[projectOffset(letter)];
  return v === null || v === undefined ? "" : String(v).trim();
}

export function rowToProject(row: unknown[], sheetRow: number): Project {
  return {
    id: projectText(row, PROJECT_COLUMNS.id),
    name: projectText(row, PROJECT_COLUMNS.name),
    color: projectText(row, PROJECT_COLUMNS.color),
    archived: projectText(row, PROJECT_COLUMNS.archived).toUpperCase() === "TRUE",
    mindmap: projectText(row, PROJECT_COLUMNS.mindmap),
    taskFile: projectText(row, PROJECT_COLUMNS.taskFile),
    driveFolder: projectText(row, PROJECT_COLUMNS.driveFolder),
    sheetRow,
  };
}

export function projectToRow(project: Project): CellValue[] {
  return [
    project.id,
    project.name,
    project.color,
    project.archived ? "TRUE" : "FALSE",
    project.mindmap,
    project.taskFile,
    project.driveFolder,
  ];
}

export function projectsReadRange(tab: string): string {
  return `${tab}!${PROJECTS_READ_FIRST}${PROJECTS_FIRST_DATA_ROW}:${PROJECTS_READ_LAST}`;
}

export function projectRowRange(tab: string, row: number): string {
  return `${tab}!${PROJECTS_READ_FIRST}${row}:${PROJECTS_READ_LAST}${row}`;
}

export function projectsHeaderRange(tab: string): string {
  return `${tab}!${PROJECTS_READ_FIRST}${PROJECTS_HEADER_ROW}:${PROJECTS_READ_LAST}${PROJECTS_HEADER_ROW}`;
}
